import { useEffect, useState } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

// Duration of the fade/scale "sunset" — must match the transition classes below.
const SUNSET_MS = 700;

/**
 * PaginationLoader
 *
 * Shows the meditating-giraffe Lottie while the next page is being fetched,
 * then slowly "sunsets" — fades + eases out — once loading finishes, instead
 * of vanishing abruptly. Shared by the product grid (App.js) and the stories
 * feed (Stories.js) so pagination feels the same everywhere.
 *
 * Drive it with the `active` prop (typically `loadingMore`). It self-manages
 * mount/unmount: it stays mounted through the fade-out so the exit animation
 * can play, then removes itself. The caller decides how long loading lasts —
 * e.g. the product grid holds `loadingMore` for a minimum beat so the giraffe
 * shows below the current products before the next batch is revealed.
 *
 * The animation is intrinsically square; it renders at 96px on mobile and
 * 128px on desktop so it reads clearly without dominating the viewport.
 */
export default function PaginationLoader({ active, className = '' }) {
  const [mounted, setMounted] = useState(active);
  const [visible, setVisible] = useState(active);

  useEffect(() => {
    if (active) {
      // Mount first, then flip to visible on the next frame so the CSS
      // transition has a "from" state to animate out of (fade-in).
      setMounted(true);
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    }

    // Loading finished — sunset: fade/scale out, then unmount after the
    // transition completes.
    setVisible(false);
    const unmountTimer = setTimeout(() => setMounted(false), SUNSET_MS);
    return () => clearTimeout(unmountTimer);
  }, [active]);

  if (!mounted) return null;

  return (
    <div
      className={`flex flex-col items-center justify-center transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-2 scale-95'
      } ${className}`}
      data-testid="pagination-lottie-loader"
      role="status"
      aria-live="polite"
      aria-busy={active}
    >
      <DotLottieReact
        src="/meditating-giraffe.lottie"
        loop
        autoplay
        className="w-24 h-24 md:w-32 md:h-32 leading-none"
        aria-label="Loading more"
      />
      <span className="sr-only">Loading more…</span>
    </div>
  );
}
